import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";

import { detectionSourceLabel, isAutoDetected } from "../../DTOs/Common/DetectionSource";
import { GeoRehandleDTO } from "../../DTOs/Geology/GeoRehandleDTO";
import { geologyAjax } from "../../ajax/GeologyAjax";
import { ButtonCrumb } from "../../components/Breadcrumbs/ButtonCrumb";
import { DateSelectCrumb } from "../../components/Breadcrumbs/DateSelectCrumb";
import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import { useDataTable, type DataTableFilteredRequest } from "../../components/DataTable/TableHook";
import { useAutoPageSize } from "../../components/Hooks/useAutoPageSize";
import { setCurrentView } from "../../state/reducers/viewReducer";
import { RootState } from "../../state/store";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { useOptions } from "../../components/Hooks/useOptions";
import { useOnlineStatus } from "../../components/Hooks/useOnlineStatus";
import { useMovementEntryConfig } from "../../components/Hooks/useMovementEntryConfig";
import { useTelemetryEnabled } from "../../components/Hooks/useTelemetryEnabled";
import { RehandleGroupsTable } from "./RehandleGroupsTable";
import { sumCachedLoads } from "../../offline/patchCachedDiggingSummary";
import { InsertGeoRehandleRow } from "./InsertGeologyRehandleRow";
import { DeleteButton } from "../../components/Buttons/DeleteButton";
import Button from "../../components/Buttons/Button";
import SaveButton from "../../components/Buttons/SaveButton";
import { VerificationIndicator } from "../../components/VerificationIndicator/VerificationIndicator";
import { GeoRehandleDiggerDTO } from "../../DTOs/Geology/GeoRehandleDiggerDTO";
import { GeoRehandleTruckingTruckDTO } from "../../DTOs/Geology/GeoRehandleTruckingTruckDTO";
import { setSelectedDate, setSelectedShift } from "../../state/reducers/shiftReducer";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { TripleShift } from "../../types/Shift";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import DiggerCell from "./TableCellsComponents/DiggerCell";
import PlantModelSelect from "./TableCellsComponents/PlantModelSelect";
import TruckSelect from "./TableCellsComponents/TruckSelect";
import ShiftHourSelect from "./TableCellsComponents/ShiftHourSelect";
import MovementTimeSelect from "./TableCellsComponents/MovementTimeSelect";
import MovementFromCell from "./TableCellsComponents/MovementFromCell";
import MovementToCell from "./TableCellsComponents/MovementToCell";
import { ShiftSelectCrumb } from "../../components/Breadcrumbs/Shift/ShiftSelectCrumb";
import { GetGeoRehandlesPagedResultDTO } from "../../DTOs/Geology/GetGeoRehandlesPagedResultDTO";
import GeologyRehandleTableFooter from "./GeologyRehandleTableFooter";
import ShiftTableNoResults from "../../components/ShiftTableNoResults/ShiftTableNoResults";
import { TablePage } from "../../components/Layout/TablePage";
import { useSignalRContext } from "../../components/Contexts/SignalRContext";
import { geoRehandleMutations } from "../../offline/geoRehandleMutations";
import { useIsReadOnly } from "../../utilities/helpers/roleUtilities";
import { movementColumnLabel, movementEntryErrors, movementLayout, plantModelOptions, truckOptions } from "./movementEntry";
import { useMovementTiming } from "./useMovementTiming";

type GeologyRehandleTableMetaData = {
  totalLoads: number;
  totalTonnes: number;
}

const GeologyRehandle: React.FC = () => {
  const { t } = useTranslation("geology");
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const isReadOnly = useIsReadOnly();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { connection } = useSignalRContext();
  const queryClient = useQueryClient();
  const { selectedDate, selectedShift } = useShiftTimes();
  const isOnline = useOnlineStatus();
  const [localSelectedShift, _setLocalSelectedShift] = useState<TripleShift>(selectedShift)

  const setLocalSelectedShift = (newShift: TripleShift) => {
    _setLocalSelectedShift(newShift);

    if (newShift === "DAY" || newShift === "NIGHT") {
      dispatch(setSelectedShift(newShift));
    }
  };

  const [insertRowState, setInsertRowState] = useState<GeoRehandleDTO>(new GeoRehandleDTO({
    geoRehandleId: 0,
    operationId: 0,
    bucketTypeId: 0,
    movementFromId: 0,
    movementToId: 0
  }));

  const {
    options: diggerOptions,
    isLoading: isDiggerOptionsLoading
  } = useOptions<GeoRehandleDiggerDTO, GeoRehandleDiggerDTO>(
    ['geology', 'rehandleDiggers', selectedDate.toISODate(), localSelectedShift],
    () => geologyAjax.getRehandleDiggers(selectedDate, localSelectedShift),
    x => x,
  );

  const isLoaderOperation = useCallback(
    (operationId?: number) => diggerOptions.find(d => d.operationId === operationId)?.isLoader ?? false,
    [diggerOptions]);

  const entryConfig = useMovementEntryConfig('rehandle');
  const telemetryEnabled = useTelemetryEnabled();
  const layout = movementLayout(entryConfig);
  const { zone, hours, liveHourIndex, shiftWindow } = useMovementTiming(selectedDate, localSelectedShift);
  const defaultInsertHour = layout.timeColumn === "Hour" && liveHourIndex >= 0 ? liveHourIndex + 1 : undefined;

  // Note: not persisted. Grouping is how somebody wants to look at THIS shift right now - a shift of
  // telemetry wants collapsing, a shift of hand-entered rows does not - so remembering the choice across
  // dates would as often be wrong as right.
  const [isGrouped, setIsGrouped] = useState(false);

  const {
    options: rehandleTrucks,
    isLoading: isTruckOptionsLoading
  } = useOptions<GeoRehandleTruckingTruckDTO, GeoRehandleTruckingTruckDTO>(
    ['geology', 'rehandleTrucks', selectedDate.toISODate()],
    () => geologyAjax.getRehandleTruckingTrucks(selectedDate),
    x => x,
    { enabled: entryConfig.carriedBy !== 'DiggerLoader' || isGrouped },
  );

  const trucks = useMemo(() => truckOptions(rehandleTrucks), [rehandleTrucks]);
  const plantModels = useMemo(() => plantModelOptions(rehandleTrucks), [rehandleTrucks]);

  const {
    options: rehandleMovementFromLocationOptions,
    isLoading: isRehandleMovementFromLocationOptionsLoading
  } = useOptions<LocationDTO, { value: number, label: string, isDumpOrCrusher: boolean }>(
    ['geology', 'rehandleFromLocations', selectedDate.toISODate()],
    () => geologyAjax.getRehandleMovementFromLocations(selectedDate),
    x => ({
      value: x.id ?? 0,
      label: x.name,
      isDumpOrCrusher: x.locationTypeId === 3 || x.locationTypeId === 6
    }),
  );

  const {
    options: rehandleMovementToLocationOptions,
    isLoading: isRehandleMovementToLocationOptionsLoading
  } = useOptions<LocationDTO, { value: number, label: string, isDumpOrCrusher: boolean }>(
    ['geology', 'rehandleToLocations', selectedDate.toISODate()],
    () => geologyAjax.getRehandleMovementToLocations(selectedDate),
    x => ({
      value: x.id ?? 0,
      label: x.name,
      isDumpOrCrusher: x.locationTypeId === 3 || x.locationTypeId === 6
    }),
  );

  const getRows = useCallback((req: DataTableFilteredRequest) => geologyAjax.getGeoRehandles(selectedDate, localSelectedShift, req), [selectedDate, localSelectedShift]);

  const getGeologyRehandleTableMetaData = useCallback((response: GetGeoRehandlesPagedResultDTO) => ({
    totalLoads: response.totalLoads,
    totalTonnes: response.totalTonnes,
  }), []);

  const { pageSize: fitPageSize, onAutoPageSize } = useAutoPageSize();

  const {
    rows,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    pageSize,
    totalPages,
    totalCount,
    sortField,
    sortDirection,
    setPage,
    appendRowSource,
    removeRowSource,
    updateRowSource,
    invalidate,
    tableMetaData
  } = useDataTable<GeoRehandleDTO, GeologyRehandleTableMetaData, GetGeoRehandlesPagedResultDTO>({
    queryKey: ['geology', 'rehandles', selectedDate.toISODate(), localSelectedShift],
    fetchFn: getRows,
    initialPage: 1,
    pageSize: fitPageSize,
    clientPaginate: true,
    selectMeta: getGeologyRehandleTableMetaData
  });

  const handleUpdateRow = useCallback(async (row: GeoRehandleDTO) => {
    let isValid = true;

    if (!row.operationId) {
      isValid = false;
      enqueueSnackbar(t("movement.selectDigger"), { variant: 'error' });
    }
    movementEntryErrors(row, entryConfig, false, { plantModels, trucks, carriesOwnLoad: isLoaderOperation(row.operationId) })
      .forEach(message => {
        isValid = false;
        enqueueSnackbar(message, { variant: 'error' });
      });
    if (!row.movementFromId) {
      isValid = false;
      enqueueSnackbar(t("movement.selectFrom"), { variant: 'error' });
    }
    if (!row.loads) {
      isValid = false;
      enqueueSnackbar(t("movement.enterLoads"), { variant: 'error' });
    }
    if (!row.movementToId) {
      isValid = false;
      enqueueSnackbar(t("movement.selectTo"), { variant: 'error' });
    }
    if (row.movementFromId === row.movementToId) {
      isValid = false;
      enqueueSnackbar(t("movement.selectDifferentTo"), { variant: 'error' });
    }

    if (!isValid || row.geoRehandleId === 0) return;

    const response = await geoRehandleMutations.updateGeoRehandle(row)

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      invalidate();
      return false;
    }

    enqueueSnackbar(t("rehandle.updated"), { variant: 'success' });
    const { rehandle, totalLoads, totalTonnes } = response.data!;
    updateRowSource((r) => r.geoRehandleId === rehandle!.geoRehandleId, rehandle!, { totalLoads, totalTonnes });

    return true;
  }, [entryConfig, plantModels, trucks, isLoaderOperation, enqueueSnackbar, updateRowSource, invalidate, t]);

  const handleAddRow = useCallback(async () => {
    let isValid = true;
    const row = { ...insertRowState, hour: insertRowState.hour ?? defaultInsertHour };

    if (!row.operationId) {
      enqueueSnackbar(t("movement.selectDigger"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    movementEntryErrors(row, entryConfig, true, { plantModels, trucks, carriesOwnLoad: isLoaderOperation(row.operationId) })
      .forEach(message => {
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
        isValid = false;
      });

    if (!row.movementFromId) {
      enqueueSnackbar(t("movement.selectFrom"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!row.loads) {
      enqueueSnackbar(t("movement.enterLoads"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!row.movementToId) {
      enqueueSnackbar(t("movement.selectTo"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!isValid) return false;

    const response = await geoRehandleMutations.addGeoRehandle(row, selectedDate, localSelectedShift);

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 * (index + 1) });
      });
      return false;
    }

    enqueueSnackbar(t("rehandle.added"), { variant: 'success' });

    const { rehandle, totalLoads, totalTonnes } = response.data!;
    appendRowSource(rehandle!, { totalLoads, totalTonnes });
    setInsertRowState(new GeoRehandleDTO({
      geoRehandleId: 0,
      operationId: 0,
      bucketTypeId: 0,
      movementFromId: 0,
      movementToId: 0,
      hour: row.hour
    }));
    return true;
  }, [insertRowState, defaultInsertHour, entryConfig, plantModels, trucks, isLoaderOperation, selectedDate, localSelectedShift, enqueueSnackbar, appendRowSource, t]);

  const deleteRow = useCallback(async (rowId: number) => {
    const response = await geoRehandleMutations.deleteGeoRehandle(rowId);

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    const { totalLoads, totalTonnes } = response.data!;
    removeRowSource((r) => r.geoRehandleId === rowId, { totalLoads, totalTonnes });
    enqueueSnackbar(t("rehandle.deleted"), { variant: 'success', autoHideDuration: 3000 })

    return true;
  }, [enqueueSnackbar, removeRowSource, t]);

  const verifyRow = useCallback(async (entry: GeoRehandleDTO) => {
    let response;
    try {
      response = await geologyAjax.verifyGeoRehandle(entry.geoRehandleId!);
    } catch {
      enqueueSnackbar(t("movement.acceptUnreachable"), { variant: "error", autoHideDuration: 6000 });
      return;
    }

    if (!response.success) {
      response.errors?.forEach(error =>
        enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 6000 }));
      return;
    }

    updateRowSource(
      (r: GeoRehandleDTO) => r.geoRehandleId === entry.geoRehandleId,
      { ...entry, isVerified: true } as GeoRehandleDTO);
    invalidate();

    enqueueSnackbar(t("movement.rowAccepted"), { variant: "success", autoHideDuration: 2500 });
  }, [enqueueSnackbar, invalidate, updateRowSource, t]);

  const translateRows = useCallback((entry: GeoRehandleDTO): TableRow => {
    return {
      id: entry.geoRehandleId ?? 0,
      values: [
        ...(layout.timeColumn === "Hour" ? [{
          name: "Hour",
          editable: false,
          type: "custom" as const,
          value: entry.hour,
          tailwindClasses: `${layout.time} py-1 pl-1`,
          render: () => (
            <ShiftHourSelect
              hour={entry.hour}
              hours={hours}
              liveHourIndex={liveHourIndex}
              disabled={isReadOnly}
              onChange={hour => handleUpdateRow({ ...entry, hour })}
            />
          )
        }] : []),
        ...(layout.timeColumn === "Time" ? [{
          name: "Time",
          editable: false,
          type: "custom" as const,
          value: entry.movementTimeUtc,
          tailwindClasses: `${layout.time} py-1 pl-1`,
          render: () => (
            <MovementTimeSelect
              movementTimeUtc={entry.movementTimeUtc}
              shiftWindow={shiftWindow}
              zone={zone}
              disabled={isReadOnly}
              saveOnAccept
              onChange={movementTimeUtc => handleUpdateRow({ ...entry, movementTimeUtc })}
            />
          )
        }] : []),
        {
          name: "Digger",
          editable: false,
          type: "custom",
          value: entry.operationId,
          tailwindClasses: `${layout.machine} py-1${layout.timeColumn ? "" : " pl-1"}`,
          render: () => (
            <DiggerCell
              entry={entry}
              diggerOptions={diggerOptions}
              isDiggerOptionsLoading={isDiggerOptionsLoading}
              handleUpdateRow={handleUpdateRow}
              isReadOnly={isReadOnly}
            />
          )
        },
        ...(layout.carrierColumn === "Plant Model" ? [{
          name: "Plant Model",
          editable: false,
          type: "custom" as const,
          value: entry.bucketTypeId,
          tailwindClasses: `${layout.carrier} py-1`,
          render: () => (
            <PlantModelSelect
              bucketTypeId={entry.bucketTypeId}
              plantModels={plantModels}
              isLoading={isTruckOptionsLoading}
              disabled={isReadOnly}
              onChange={bucketTypeId => handleUpdateRow({ ...entry, bucketTypeId })}
              allowOwnLoad={isLoaderOperation(entry.operationId)}
              onOwnLoad={() => handleUpdateRow({ ...entry, bucketTypeId: undefined })}
            />
          )
        }] : []),
        ...(layout.carrierColumn === "Truck" ? [{
          name: "Truck",
          editable: false,
          type: "custom" as const,
          value: entry.truckPlantId,
          tailwindClasses: `${layout.carrier} py-1`,
          render: () => (
            <TruckSelect
              truckPlantId={entry.truckPlantId}
              trucks={trucks}
              isLoading={isTruckOptionsLoading}
              disabled={isReadOnly}
              onChange={truck => handleUpdateRow({ ...entry, truckPlantId: truck.plantId, bucketTypeId: truck.bucketTypeId })}
              allowOwnLoad={isLoaderOperation(entry.operationId)}
              onOwnLoad={() => handleUpdateRow({ ...entry, truckPlantId: undefined, bucketTypeId: undefined })}
            />
          )
        }] : []),
        {
          name: "Movement From",
          editable: false,
          type: "custom",
          value: entry.movementFromId,
          tailwindClasses: layout.movement,
          render:
            () => (
              <MovementFromCell
                entry={entry}
                locationOptions={rehandleMovementFromLocationOptions}
                locationOptionsLoading={isRehandleMovementFromLocationOptionsLoading}
                handleUpdateRow={handleUpdateRow}
                isReadOnly={isReadOnly}
              />
            )
        },
        {
          name: "Loads",
          editable: true,
          type: "integer",
          value: entry.loads,
          tailwindClasses: "basis-[10%] py-1",
          disabled: isReadOnly,
          onChangeDebounced: async (_, newValue) => await handleUpdateRow({ ...entry, loads: newValue }),
          onChangeDebouncedMs: 1200,
        },
        {
          name: "Movement To",
          editable: false,
          type: "custom",
          value: entry.movementToId,
          tailwindClasses: layout.movement,
          render:
            () => (
              <MovementToCell
                entry={entry}
                locationOptions={rehandleMovementToLocationOptions}
                locationOptionsLoading={isRehandleMovementToLocationOptionsLoading}
                handleUpdateRow={handleUpdateRow}
                isReadOnly={isReadOnly}
              />
            )
        },
        ...(telemetryEnabled ? [{
          // Note: the same shape Stoppages.tsx and TrucksTable.tsx use for provenance, so an inferred
          // rehandle reads the way inferred data reads everywhere else in Eclipse rather than announcing
          // itself a third way. Both this screen and Crushing show it: they are the same tblGeoRehandle
          // rows split on whether the destination is a crusher, so marking only one would leave inferred
          // stockpile-to-crusher rows looking hand-entered.
          name: 'Source',
          type: 'custom' as const,
          value: entry.isVerified === false ? 'Needs checking' : '',
          tailwindClasses: 'w-full basis-[10%]',
          render: () => (
            <div className="flex items-center justify-center gap-1.5 w-full">
              <div className="text-theme-text-muted text-xs">
                {detectionSourceLabel(entry.detectionSource)}
              </div>
              {/* Note: the mark IS the accept control. Clicking it accepts the row as it stands, which is
                  the only way a row the FMS got right ever leaves the queue - editing clears the flag too,
                  but only as a side effect of changing something. */}
              {isAutoDetected(entry.detectionSource) && (
                <VerificationIndicator
                  isVerified={entry.isVerified !== false}
                  onAccept={() => verifyRow(entry)}
                  subject={t("rehandle.verifySubject")}
                  disabled={isReadOnly || !isOnline}
                />
              )}
            </div>
          )
        }] : []),
        {
          name: 'Actions',
          type: 'custom',
          value: 'custom',
          tailwindClasses: 'basis-[10%] pr-1',
          render: (_, tableRow) =>
            <div className="flex justify-center flex-wrap gap-1">
              <DeleteButton
                handleDelete={() => deleteRow(tableRow.id)}
                disabled={isReadOnly}
              />
            </div>
        }
      ]
    }
  }, [
    layout,
    hours,
    liveHourIndex,
    shiftWindow,
    zone,
    plantModels,
    trucks,
    isTruckOptionsLoading,
    isLoaderOperation,
    telemetryEnabled,
    rehandleMovementToLocationOptions,
    rehandleMovementFromLocationOptions,
    isRehandleMovementToLocationOptionsLoading,
    isRehandleMovementFromLocationOptionsLoading,
    diggerOptions,
    isDiggerOptionsLoading,
    handleUpdateRow,
    deleteRow,
    isReadOnly,
    isOnline,
    verifyRow,
    t
  ]);

  const displayData = useMemo(() => rows.map(translateRows), [rows, translateRows]);
  const machineLabel = useCallback(
    (operationId: number) => diggerOptions.find(d => d.operationId === operationId)?.plantRef ?? "-",
    [diggerOptions]);

  const locationLabel = useCallback((locationId?: number) => {
    if (locationId == null) return "-";
    return rehandleMovementFromLocationOptions.find(l => l.value === locationId)?.label
      ?? rehandleMovementToLocationOptions.find(l => l.value === locationId)?.label
      ?? "-";
  }, [rehandleMovementFromLocationOptions, rehandleMovementToLocationOptions]);

  const bucketTypeLabel = useCallback((bucketTypeId?: number) => {
    if (bucketTypeId == null) return "-";
    return plantModels.find(m => m.bucketTypeId === bucketTypeId)?.bucketType ?? "-";
  }, [plantModels]);

  const handleAcceptAll = useCallback(async () => {
    const response = await geologyAjax.verifyAllGeoRehandles(selectedDate, localSelectedShift, false);

    if (!response.success) {
      response.errors?.forEach((error, i) =>
        enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 4000 + i * 1000 }));
      return false;
    }

    const accepted = response.data ?? 0;
    enqueueSnackbar(
      accepted === 0 ? t("movement.nothingToCheck")
        : t("movement.rowsAccepted", { count: accepted }),
      { variant: "success", autoHideDuration: 3000 });

    invalidate();
    return true;
  }, [selectedDate, localSelectedShift, enqueueSnackbar, invalidate, t]);

  useEffect(() => {
    const newView = "GeologyRehandle";

    if (currentView !== newView) {
      dispatch(setCurrentView(newView));
    }
  }, [currentView, dispatch]);

  const handleGeoMovementsChanged = useCallback(() => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: ['geology', 'rehandleFromLocations'] });
    queryClient.invalidateQueries({ queryKey: ['geology', 'rehandleToLocations'] });
  }, [invalidate, queryClient]);

  useEffect(() => {
    if (!connection) return;
    connection.on("DataUpdatedAsync", handleGeoMovementsChanged);
    return () => {
      connection.off("DataUpdatedAsync", handleGeoMovementsChanged);
    };
  }, [connection, handleGeoMovementsChanged]);

  return (
    <TablePage>
      <Breadcrumbs actions={telemetryEnabled ? (
        <Button
          variant={isGrouped ? "primary" : "secondary"}
          size="sm"
          onClick={() => setIsGrouped(g => !g)}
        >
          {isGrouped ? t("movement.showingGroups") : t("movement.groupMovements")}
        </Button>
      ) : undefined}>
        <DateSelectCrumb
          selectedDate={selectedDate}
          setSelectedDate={date => dispatch(setSelectedDate(date.toISODate()!))}
        />
        <ShiftSelectCrumb
          showAfternoonShift
          selectedShift={localSelectedShift}
          setSelectedShift={setLocalSelectedShift}
        />
        <ButtonCrumb buttonText={t("rehandle.title")} />
      </Breadcrumbs>
      {telemetryEnabled && isGrouped ? (
        <RehandleGroupsTable
          selectedDate={selectedDate}
          selectedShift={localSelectedShift}
          isCrushing={false}
          machineLabel={machineLabel}
          locationLabel={locationLabel}
          bucketTypeLabel={bucketTypeLabel}
          isShiftLocked={isReadOnly}
          isOnline={isOnline}
        />
      ) : (
        <DataTable
          data={displayData}
          isLoading={isLoading}
          isBackgroundFetching={isBackgroundFetching}
          error={error}
          pageNumber={pageNumber}
          totalCount={totalCount}
          pageSize={pageSize}
          totalPages={totalPages}
          setPage={setPage}
          sortField={sortField}
          sortDirection={sortDirection}
          minTableWidthPixels={layout.timeColumn ? 1280 : 1080}
          fillHeight
          autoPageSize
          onAutoPageSize={onAutoPageSize}
          estimatedRowHeightPx={60}
          noResultsContent={<ShiftTableNoResults
            empty={t("rehandle.empty")}
            entity={t("rehandle.entity")}
            howToAdd={<Trans t={t} i18nKey="rehandle.howToAdd" components={{ b: <span className="font-bold" /> }} />}
            addWrite="Geology.InsertGeologyRehandle"
          />}
          tableHeaderContent={telemetryEnabled ? (
            <div className="flex flex-col w-full items-center fade-up">
              <div className="container overflow-y-hidden">
                <div className="bg-theme-surface-2 px-2 py-2 border border-theme-table-border mb-4 compact:mb-2 shadow text-theme-text">
                  <div className="flex items-center justify-end gap-4">
                    <span className="opacity-70 text-sm">
                      {t("movement.telemetryNote")}
                    </span>
                    <SaveButton
                      buttonLabel={t("movement.acceptAll")}
                      confirmMessage={t("rehandle.acceptAllConfirm")}
                      disabled={isReadOnly || !isOnline}
                      handleSave={handleAcceptAll}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : undefined}
          tableFooterContent={<GeologyRehandleTableFooter
            isLoading={isLoading}
            totalLoads={isOnline
              ? tableMetaData?.totalLoads
              : sumCachedLoads(['dataTable', 'geology', 'rehandles', selectedDate.toISODate(), localSelectedShift, 'all', false, null])}
          />}
          insertRowRenderer={() => <InsertGeoRehandleRow
            state={insertRowState}
            setState={setInsertRowState}
            diggerOptions={diggerOptions}
            isDiggerOptionsLoading={isDiggerOptionsLoading}
            layout={layout}
            plantModels={plantModels}
            trucks={trucks}
            isTruckOptionsLoading={isTruckOptionsLoading}
            allowOwnLoad={isLoaderOperation(insertRowState.operationId)}
            hour={insertRowState.hour ?? defaultInsertHour}
            hours={hours}
            liveHourIndex={liveHourIndex}
            shiftWindow={shiftWindow}
            zone={zone}
            rehandleMovementFromLocationOptions={rehandleMovementFromLocationOptions}
            isRehandleMovementFromLocationOptionsLoading={isRehandleMovementFromLocationOptionsLoading}
            rehandleMovementToLocationOptions={rehandleMovementToLocationOptions}
            isRehandleMovementToLocationOptionsLoading={isRehandleMovementToLocationOptionsLoading}
            onAdd={handleAddRow}
            isReadOnly={isReadOnly}
            telemetryEnabled={telemetryEnabled}
          />}
          overrideHeaderCells={[
            ...(layout.timeColumn ? [{ name: layout.timeColumn, label: movementColumnLabel(layout.timeColumn), tailwindClasses: `${layout.time} py-1 pl-1` }] : []),
            { name: "Digger", label: t("movement.digger"), tailwindClasses: `${layout.machine} py-1${layout.timeColumn ? "" : " pl-1"}` },
            ...(layout.carrierColumn ? [{ name: layout.carrierColumn, label: movementColumnLabel(layout.carrierColumn), tailwindClasses: `${layout.carrier} py-1` }] : []),
            { name: "Movement From", label: t("movement.movementFrom"), tailwindClasses: layout.movement },
            { name: "Loads", label: t("fields.loads"), tailwindClasses: "basis-[10%] py-1" },
            { name: "Movement To", label: t("movement.movementTo"), tailwindClasses: layout.movement },
            ...(telemetryEnabled ? [{ name: "Source", label: t("fields.source"), tailwindClasses: "basis-[10%]" }] : []),
            { name: "Actions", label: t("fields.actions"), tailwindClasses: "basis-[10%] pr-1" }
          ]}
        />
      )}
    </TablePage>
  )
}

export default GeologyRehandle